/**
 * The procedural SFX bank: nostalgic 16-bit arcade sounds built from ZzFX layers (ARCHITECTURE §2 "Audio").
 *
 * Each sound is a list of ZzFX layers mixed offline (with per-layer start offsets and gains) into ONE buffer the
 * first time the engine needs it, peak-normalised and then scaled by `gain`. Some sounds also reference a
 * generated FILE asset (apps/client/public/audio, see audio-manifest.json):
 *   fileMode 'layer'  → the file plays on top of the synth layers (e.g. a real pad crunch under the tackle boom)
 *   fileMode 'prefer' → the file replaces the synth layers once it is loaded (the synth is the fallback)
 *
 * Sound design notes: frequencies stay in a friendly 60 Hz–3 kHz band, everything is short (stingers ≤ 1.6 s),
 * squares/saws carry the "Genesis/SNES" flavour, sines/triangles carry weight. `pitchVar` keeps repeats lively.
 */
import type { ZzfxParams } from './zzfx';

export interface ZzfxLayer {
  readonly params: ZzfxParams;
  /** Start offset in seconds. */
  readonly at?: number;
  /** Linear gain of this layer in the mix (default 1). */
  readonly gain?: number;
}

export interface SfxDef {
  readonly layers: readonly ZzfxLayer[];
  /** Optional file asset id layered with / preferred over the synth layers. */
  readonly file?: string;
  readonly fileMode?: 'layer' | 'prefer';
  /** Gain of the file layer (default 1). */
  readonly fileGain?: number;
  /** Output gain after peak normalisation (default 0.5). */
  readonly gain?: number;
  /** Random ± playback-rate variation (fraction, default 0.03). */
  readonly pitchVar?: number;
  /** Simultaneous voices of this id (default 3); the oldest is stolen. */
  readonly maxVoices?: number;
  /** Minimum time between two starts of this id (default 30 ms). */
  readonly cooldownMs?: number;
  /** Voice-stealing priority 0..3 (default 1): when the pool is full, lower priority voices are stolen first. */
  readonly priority?: number;
  /** Menu/UI sound: bypasses the "muffled" pause filter. */
  readonly ui?: boolean;
}

/** Shorthand: a ZzFX parameter list (sparse arrays are fine: holes = defaults). */
const z = (...p: (number | undefined)[]): ZzfxParams => p;
const L = (params: ZzfxParams, at = 0, gain = 1): ZzfxLayer => ({ params, at, gain });

// Musical helpers (Hz)
const N = {
  C4: 261.63,
  Cs4: 277.18,
  D4: 293.66,
  Eb4: 311.13,
  E4: 329.63,
  G3: 196,
  A3: 220,
  C5: 523.25,
  E5: 659.25,
  G4: 392,
  G5: 783.99,
  A4: 440,
  B5: 987.77,
  C6: 1046.5,
  D6: 1174.66,
  E6: 1318.51,
  G6: 1567.98,
} as const;

/** A short brass-ish note: saw + square, quick attack, slight vibrato. */
const brass = (freq: number, at: number, hold: number, gain = 1): ZzfxLayer[] => [
  L(z(1, 0, freq, 0.012, hold, 0.12, 2, 1.2, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0.85, 0.03), at, 0.7 * gain),
  L(z(1, 0, freq * 1.003, 0.012, hold, 0.1, 5, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0.7, 0.03), at, 0.35 * gain),
];
/** A bell/chime partial pair (sine + triangle octave below). */
const bell = (freq: number, at: number, len = 0.28, gain = 1): ZzfxLayer[] => [
  L(z(1, 0, freq, 0, 0.02, len, 0, 1), at, 0.8 * gain),
  L(z(1, 0, freq / 2, 0, 0.02, len * 0.8, 1, 1), at, 0.35 * gain),
];
/** Noise snare hit for fanfares. */
const snare = (at: number, gain = 1): ZzfxLayer =>
  L(z(1, 0, 900, 0, 0.01, 0.09, 4, 1, -2, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 600), at, 0.35 * gain);
/** Low sub boom. */
const boom = (at = 0, freq = 58, len = 0.32, gain = 1): ZzfxLayer =>
  // slide (×500 Hz/s) drops the pitch by ~45 % over the sound: a thump, never a negative frequency.
  L(z(1, 0, freq, 0, 0.05, len, 0, 1, -(freq * 0.45) / (0.05 + len) / 500), at, gain);

export const SFX_BANK = {
  // ── Movement ─────────────────────────────────────────────────────────────────────────────────────────
  /** Hop-run footstep "pup" (very quiet; fires every hop). */
  hop: {
    layers: [L(z(1, 0, 150, 0.004, 0.012, 0.05, 1, 1.2, 4, 0, 0, 0, 0, 0, 0, 0, 0, 0.5))],
    gain: 0.16,
    pitchVar: 0.12,
    maxVoices: 2,
    cooldownMs: 70,
    priority: 0,
  },
  /** Classic rising "boing". */
  jump: {
    layers: [L(z(1, 0, 230, 0.01, 0.05, 0.15, 1, 1.5, 5, 0, 0, 0, 0, 0, 0, 0, 0, 0.8))],
    gain: 0.42,
    pitchVar: 0.05,
    maxVoices: 2,
  },
  /** Carrier jump (hurdle attempt): boing + air whoosh. */
  hurdle: {
    layers: [
      L(z(1, 0, 230, 0.01, 0.05, 0.15, 1, 1.5, 5, 0, 0, 0, 0, 0, 0, 0, 0, 0.8)),
      L(z(1, 0, 420, 0.03, 0.08, 0.14, 4, 1, 3, 0, 0, 0, 0, 0, 0, 0, 0, 0.6, 0, 0, -2600), 0, 0.6),
    ],
    gain: 0.5,
    maxVoices: 2,
  },
  /** Landing thump + dust (volume scaled by impact). */
  land: {
    layers: [
      L(z(1, 0, 85, 0, 0.02, 0.12, 0, 2, -0.3, 0, 0, 0, 0, 0.3, 0, 0, 0, 0.6)),
      L(z(1, 0, 700, 0, 0.01, 0.07, 4, 1, -5, 0, 0, 0, 0, 0, 0, 0, 0, 0.5, 0, 0, -1800), 0, 0.45),
    ],
    gain: 0.45,
    pitchVar: 0.08,
    maxVoices: 2,
    cooldownMs: 60,
  },
  /** Dash burst: short whoosh with a sneaker chirp. */
  dash: {
    layers: [
      L(z(1, 0, 650, 0.01, 0.06, 0.14, 4, 1, -4, 0, 0, 0, 0, 0, 0, 0, 0, 0.7, 0, 0, -3000)),
      L(z(1, 0, 1300, 0, 0.012, 0.03, 5, 1, 18), 0, 0.18),
    ],
    gain: 0.42,
    pitchVar: 0.06,
    maxVoices: 2,
  },
  /** Fast-fall: descending slide whistle. */
  fast_fall: { layers: [L(z(1, 0, 900, 0.01, 0.07, 0.05, 0, 1, -11))], gain: 0.3, maxVoices: 2 },
  /** Fake-jump cancel: freeze tick, then the WHAM slam (square drop + crunch + sub boom). */
  fake_cancel: {
    layers: [
      L(z(1, 0, 1500, 0, 0.02, 0.02, 5, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1)), // the 3 f freeze "tick"
      L(z(1, 0, 170, 0, 0.05, 0.3, 5, 1, -0.4, 0, 0, 0, 0, 0.3, 0, 0.2, 0, 0.8), 0.05, 0.55), // WHAM body
      L(z(1, 0, 320, 0, 0.04, 0.2, 4, 1, -2, 0, 0, 0, 0, 0, 0, 0.1), 0.05, 0.5), // crunch
      boom(0.05, 55, 0.4, 0.95),
    ],
    gain: 0.75,
    priority: 2,
    maxVoices: 2,
  },
  /** Stutter step: sneaker squeak. */
  stutter: {
    layers: [
      L(z(1, 0, 1750, 0.005, 0.04, 0.05, 0, 1, -16, 0, 0, 0, 0, 0, 30)),
      L(z(1, 0, 900, 0, 0.02, 0.05, 4, 1, -4, 0, 0, 0, 0, 0, 0, 0, 0, 0.5, 0, 0, 1200), 0, 0.3),
    ],
    gain: 0.34,
    pitchVar: 0.07,
    maxVoices: 2,
  },
  /** Hesitation burst: tick… ZIP. */
  hesitation: {
    layers: [L(z(1, 0, 320, 0, 0.01, 0.03, 5)), L(z(1, 0, 400, 0.01, 0.05, 0.06, 2, 1, 14), 0.035, 0.6)],
    gain: 0.3,
    maxVoices: 2,
  },
  /** Spin move: pulsing whoosh "whu-whu-whu" + rising sweep. */
  spin: {
    layers: [
      L(z(1, 0, 520, 0.03, 0.2, 0.14, 4, 1, 0, 0, 0, 0, 0.07, 0, 0, 0, 0, 0.8, 0, 0.6, -4000)),
      L(z(1, 0, 300, 0.02, 0.18, 0.1, 1, 1, 6), 0, 0.25),
    ],
    gain: 0.45,
    maxVoices: 2,
  },
  /** Spin i-frames dodged a lunge: zwip + sparkle. */
  spin_dodge: {
    layers: [
      L(z(1, 0, 1800, 0, 0.03, 0.1, 0, 1, 0, 0, 600, 0.04)),
      L(z(1, 0, 600, 0.01, 0.05, 0.1, 4, 1, 8, 0, 0, 0, 0, 0, 0, 0, 0, 0.6, 0, 0, -3500), 0, 0.5),
    ],
    gain: 0.4,
    priority: 2,
  },
  /** Stumble: wobbly "bwoop". */
  stumble: {
    layers: [L(z(1, 0, 480, 0.01, 0.12, 0.14, 1, 1, -3, 0, 0, 0, 0, 0, 8))],
    gain: 0.3,
    maxVoices: 2,
  },
  /** Truck charge: low engine rev. */
  truck_start: {
    layers: [L(z(1, 0, 70, 0.03, 0.22, 0.1, 2, 1, 0.9, 0, 0, 0, 0.03, 0.2, 0, 0, 0, 0.8, 0, 0.4, -1600))],
    gain: 0.4,
    maxVoices: 2,
  },
  /** Stiff-arm connects: POW. */
  stiff_arm_hit: {
    layers: [
      L(z(1, 0, 190, 0, 0.03, 0.14, 0, 1, -1, 0, 0, 0, 0, 0.5)),
      L(z(1, 0, 1200, 0, 0.01, 0.07, 4, 1, -10), 0, 0.5),
      boom(0, 70, 0.18, 0.6),
    ],
    gain: 0.6,
    priority: 2,
  },
  /** Stiff-arm swing that misses. */
  stiff_arm: {
    layers: [L(z(1, 0, 520, 0.01, 0.04, 0.08, 4, 1, -5, 0, 0, 0, 0, 0, 0, 0, 0, 0.6, 0, 0, -3000))],
    gain: 0.28,
  },
  /** Dive: low swoosh. */
  dive: {
    layers: [L(z(1, 0, 300, 0.02, 0.1, 0.12, 4, 1, -2, 0, 0, 0, 0, 0, 0, 0, 0, 0.7, 0, 0, -1800))],
    gain: 0.38,
    pitchVar: 0.06,
  },
  /** Leap: rising swoosh + small boing. */
  leap: {
    layers: [
      L(z(1, 0, 300, 0.03, 0.1, 0.1, 4, 1, 6, 0, 0, 0, 0, 0, 0, 0, 0, 0.7, 0, 0, -2600)),
      L(z(1, 0, 260, 0.01, 0.04, 0.12, 1, 1.5, 5), 0, 0.4),
    ],
    gain: 0.4,
  },
  /** Fake dive: reverse "fwip". */
  fake_dive: {
    layers: [L(z(1, 0, 220, 0.07, 0, 0.02, 4, 1, 10, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, -3000))],
    gain: 0.32,
  },
  /** A lunge hit nothing: thin swish + tiny comedic droop. */
  whiff: {
    layers: [
      L(z(1, 0, 560, 0.01, 0.06, 0.1, 4, 1, -4, 0, 0, 0, 0, 0, 0, 0, 0, 0.5, 0, 0, -3500)),
      L(z(1, 0, 320, 0.01, 0.08, 0.1, 1, 1, -1.5), 0.05, 0.35),
    ],
    gain: 0.32,
  },
  /** Mirage decoy popped: poof + sparkle. */
  decoy_pop: {
    layers: [
      L(z(1, 0, 600, 0, 0.03, 0.25, 4, 1, 4, 0, 0, 0, 0, 0, 0, 0.05)),
      L(z(1, 0, 2400, 0, 0.02, 0.2, 0, 1, 0, 0, -800, 0.05), 0.02, 0.4),
    ],
    gain: 0.45,
    priority: 2,
  },
  /** Stunned: cartoon birdies. */
  stun: { layers: [L(z(1, 0, 2000, 0.01, 0.3, 0.06, 0, 1, 18, 0, 0, 0, 0.08))], gain: 0.16, maxVoices: 1 },

  // ── Passing ──────────────────────────────────────────────────────────────────────────────────────────
  /** QB starts charging a throw: short wind-up riser. */
  throw_charge: {
    layers: [L(z(1, 0, 200, 0.12, 0.12, 0.05, 1, 1, 3, 0, 0, 0, 0, 0, 0, 0, 0, 0.6))],
    gain: 0.18,
    maxVoices: 1,
  },
  /** Throw: pop + whoosh (bridge scales by charge). */
  throw: {
    layers: [
      L(z(1, 0, 300, 0, 0.01, 0.06, 0, 1, -3)),
      L(z(1, 0, 800, 0.01, 0.08, 0.18, 4, 1, -4, 0, 0, 0, 0, 0, 0, 0, 0, 0.7, 0, 0, -4000), 0, 0.8),
    ],
    gain: 0.48,
    pitchVar: 0.04,
  },
  /** Hail Mary launch: whoosh + heavenly chord with echo. */
  throw_hail_mary: {
    layers: [
      L(z(1, 0, 800, 0.01, 0.1, 0.25, 4, 1, -3, 0, 0, 0, 0, 0, 0, 0, 0, 0.7, 0, 0, -4000)),
      L(z(1, 0, N.C5, 0.12, 0.35, 0.6, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0.12, 0.8), 0.04, 0.4),
      L(z(1, 0, N.E5, 0.12, 0.35, 0.6, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0.12, 0.8), 0.06, 0.35),
      L(z(1, 0, N.G5, 0.12, 0.35, 0.6, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0.12, 0.8), 0.08, 0.3),
    ],
    gain: 0.55,
    priority: 3,
    maxVoices: 1,
  },
  /**
   * Kick (kickoff, punt, onside): a boot thump on leather — sub thump + punchy pop + a short slap — then the ball's
   * whoosh away. The bridge pitches it: a punt a touch lower, an onside squib higher.
   */
  kick: {
    layers: [
      boom(0, 74, 0.14, 1),
      L(z(1, 0, 190, 0, 0.012, 0.07, 0, 1, -2.5), 0, 0.85),
      L(z(1, 0, 900, 0, 0.006, 0.03, 4, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 350), 0, 0.55),
      L(z(1, 0, 520, 0.02, 0.07, 0.24, 4, 1, -3, 0, 0, 0, 0, 0, 0, 0, 0, 0.6, 0, 0, -2600), 0.035, 0.42),
    ],
    gain: 0.62,
    pitchVar: 0.04,
    priority: 2,
    maxVoices: 2,
  },
  /** Pump fake: half whoosh + glove click. */
  pump_fake: {
    layers: [
      L(z(1, 0, 600, 0.01, 0.03, 0.05, 4, 1, -8, 0, 0, 0, 0, 0, 0, 0, 0, 0.6, 0, 0, -3000)),
      L(z(1, 0, 900, 0, 0.005, 0.012, 5), 0.06, 0.4),
    ],
    gain: 0.32,
  },
  /** Catch: leather thwap + bright double chime. */
  catch: {
    layers: [
      L(z(1, 0, 140, 0, 0.02, 0.08, 0, 1, -0.8, 0, 0, 0, 0, 0.8)),
      ...bell(N.C6, 0.02, 0.16, 0.5),
      ...bell(N.G6, 0.08, 0.2, 0.4),
    ],
    gain: 0.5,
    priority: 2,
  },
  /** Interception: minor saw stab + sub boom. */
  interception: {
    layers: [
      L(z(1, 0, N.A3, 0.005, 0.12, 0.35, 2, 1, -0.3, 0, 0, 0, 0, 0, 0, 0.03, 0, 0.8)),
      L(z(1, 0, N.C4, 0.005, 0.12, 0.35, 2, 1, -0.3, 0, 0, 0, 0, 0, 0, 0.03, 0, 0.8), 0, 0.8),
      L(z(1, 0, N.E4, 0.005, 0.12, 0.35, 2, 1, -0.3, 0, 0, 0, 0, 0, 0, 0.03, 0, 0.8), 0, 0.7),
      boom(0, 55, 0.5, 0.9),
    ],
    gain: 0.55,
    priority: 3,
    maxVoices: 1,
  },
  /** Pass breakup: slap + square blip. */
  swat: {
    layers: [
      L(z(1, 0, 700, 0, 0.01, 0.06, 4, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 500)),
      L(z(1, 0, 620, 0, 0.02, 0.05, 5, 1, -10), 0, 0.6),
    ],
    gain: 0.45,
    priority: 2,
  },
  /** Incomplete: thud + bonk bounce. */
  incomplete: {
    layers: [L(z(1, 0, 120, 0, 0.02, 0.1, 0, 1, -0.5)), L(z(1, 0, 420, 0, 0.02, 0.09, 1, 1, -3), 0.13, 0.4)],
    gain: 0.4,
  },
  /** Ball bounce (bridge scales by speed). */
  ball_bounce: {
    layers: [L(z(1, 0, 165, 0, 0.015, 0.07, 1, 1, -2, 0, 0, 0, 0, 0.3))],
    gain: 0.3,
    pitchVar: 0.1,
    maxVoices: 2,
    cooldownMs: 50,
  },
  /** Goalpost DOINK: inharmonic metal clang. */
  post_hit: {
    layers: [
      L(z(1, 0, 880, 0, 0.04, 0.8, 0, 1)),
      L(z(1, 0, 885, 0, 0.04, 0.7, 0, 1), 0, 0.5),
      L(z(1, 0, 1405, 0, 0.03, 0.6, 0, 1), 0, 0.5),
      L(z(1, 0, 2350, 0, 0.02, 0.4, 0, 1), 0, 0.3),
      L(z(1, 0, 3000, 0, 0.005, 0.02, 4), 0, 0.3),
    ],
    gain: 0.5,
    priority: 3,
    maxVoices: 1,
  },

  // ── Contact ──────────────────────────────────────────────────────────────────────────────────────────
  /** Tackle: sub boom + crunch + punch, with the real pad crunch layered on top. */
  tackle: {
    layers: [
      boom(0, 60, 0.3, 1),
      L(z(1, 0, 400, 0, 0.03, 0.15, 4, 1, -4, 0, 0, 0, 0, 0, 0, 0.15), 0, 0.7),
      L(z(1, 0, 180, 0, 0.02, 0.1, 5, 1, -1.5), 0, 0.4),
    ],
    file: 'pad_hit',
    fileMode: 'layer',
    fileGain: 0.8,
    gain: 0.75,
    priority: 3,
    maxVoices: 2,
  },
  /** Counter-square tackle (leap beats hurdle / dive beats truck): tackle + metallic clank. */
  counter_tackle: {
    layers: [
      boom(0, 52, 0.4, 1),
      L(z(1, 0, 400, 0, 0.04, 0.18, 4, 1, -3, 0, 0, 0, 0, 0, 0, 0.18), 0, 0.75),
      L(z(1, 0, 1500, 0, 0.02, 0.12, 5, 1, -10), 0, 0.3),
    ],
    file: 'pad_hit',
    fileMode: 'layer',
    fileGain: 0.9,
    gain: 0.85,
    priority: 3,
    maxVoices: 2,
  },
  /** Hit-stop crunch (the impact frame). */
  hitstop: {
    layers: [L(z(1, 0, 90, 0, 0.04, 0.12, 5, 0.5, -0.5, 0, 0, 0, 0, 0.5, 0, 0.4))],
    gain: 0.36,
    maxVoices: 1,
    cooldownMs: 80,
    priority: 2,
  },
  /** Truck beat a leap: crash + debris + zoom. */
  truck_break: {
    layers: [
      L(z(1, 0, 200, 0, 0.1, 0.4, 4, 1, -0.3, 0, 0, 0, 0, 0, 0, 0.08)),
      boom(0, 50, 0.45, 1),
      L(z(1, 0, 140, 0.02, 0.12, 0.1, 2, 1, 6), 0.08, 0.35),
    ],
    gain: 0.75,
    priority: 3,
  },
  /** Bulldozer broke a tackle: crash + engine roar. */
  bulldoze: {
    layers: [
      L(z(1, 0, 200, 0, 0.1, 0.4, 4, 1, -0.3, 0, 0, 0, 0, 0, 0, 0.08)),
      boom(0, 45, 0.5, 1),
      L(z(1, 0, 60, 0.02, 0.3, 0.2, 2, 1, 0.6, 0, 0, 0, 0.03, 0.2, 0, 0, 0, 0.8, 0, 0.5, -1500), 0.05, 0.6),
    ],
    gain: 0.8,
    priority: 3,
  },
  /** Hurdle cleared a dive: whoosh + triumphant blip. */
  hurdle_clear: {
    layers: [
      L(z(1, 0, 500, 0.02, 0.08, 0.12, 4, 1, 4, 0, 0, 0, 0, 0, 0, 0, 0, 0.6, 0, 0, -3000)),
      L(z(1, 0, N.C5, 0, 0.05, 0.12, 5, 1, 0, 0, N.G5 - N.C5, 0.05), 0.03, 0.35),
    ],
    gain: 0.45,
    priority: 2,
  },

  // ── Referee & pre-snap ───────────────────────────────────────────────────────────────────────────────
  /** Referee whistle (generated file preferred; synth pea-whistle trill as fallback). */
  whistle: {
    layers: [
      L(z(1, 0, 2700, 0.01, 0.28, 0.06, 0, 1, 0, 0, 0, 0, 0.025, 0, 0, 0, 0, 1, 0, 0.5)),
      L(z(1, 0, 2790, 0.01, 0.28, 0.06, 0, 1, 0, 0, 0, 0, 0.025, 0, 0, 0, 0, 1, 0, 0.5), 0, 0.6),
    ],
    file: 'ref_whistle',
    fileMode: 'prefer',
    gain: 0.3,
    maxVoices: 1,
    cooldownMs: 250,
    pitchVar: 0.01,
  },
  /** End-of-play whistle (two short + one long). */
  whistle_long: {
    layers: [
      L(z(1, 0, 2700, 0.01, 0.06, 0.03, 0, 1, 0, 0, 0, 0, 0.025, 0, 0, 0, 0, 1, 0, 0.5)),
      L(z(1, 0, 2700, 0.01, 0.06, 0.03, 0, 1, 0, 0, 0, 0, 0.025, 0, 0, 0, 0, 1, 0, 0.5), 0.14),
      L(z(1, 0, 2700, 0.01, 0.4, 0.08, 0, 1, 0, 0, 0, 0, 0.025, 0, 0, 0, 0, 1, 0, 0.5), 0.3),
    ],
    file: 'ref_whistle_long',
    fileMode: 'prefer',
    gain: 0.28,
    maxVoices: 1,
    cooldownMs: 400,
    pitchVar: 0.01,
  },
  /** The snap: leather pop + the QB's "HIKE!". */
  snap: {
    layers: [L(z(1, 0, 240, 0, 0.01, 0.05, 0, 1, -4, 0, 0, 0, 0, 0.5)), boom(0, 80, 0.1, 0.4)],
    file: 'qb_hike',
    fileMode: 'layer',
    fileGain: 0.9,
    gain: 0.45,
    maxVoices: 1,
    priority: 2,
  },
  /** Hard count: the QB barks "HUT!" + a flinch blip. */
  hard_count: {
    layers: [L(z(1, 0, 300, 0, 0.015, 0.04, 5, 1, 6), 0, 0.5)],
    file: 'qb_hut',
    fileMode: 'layer',
    fileGain: 1,
    gain: 0.24,
    maxVoices: 1,
    priority: 2,
  },
  /** Offside: harsh beating buzzer. */
  offside_buzzer: {
    layers: [L(z(1, 0, 110, 0.01, 0.55, 0.06, 5, 1)), L(z(1, 0, 116, 0.01, 0.55, 0.06, 2, 1), 0, 0.7)],
    gain: 0.32,
    maxVoices: 1,
    priority: 3,
  },
  /** Rush count over: two-tone "go" beep. */
  rush_go: { layers: [L(z(1, 0, 880, 0, 0.06, 0.05, 5, 1, 0, 0, 440, 0.06))], gain: 0.2, maxVoices: 1 },
  /** Play clock running out: tick. */
  clock_tick: { layers: [L(z(1, 0, 1000, 0, 0.01, 0.03, 5, 1))], gain: 0.15, maxVoices: 1 },
  /** Blitz revealed at the snap: alarm siren. */
  blitz_reveal: {
    layers: [L(z(1, 0, 600, 0.01, 0.45, 0.1, 5, 1, 8, 0, 0, 0, 0.125, 0, 0, 0, 0, 1, 0, 0, -2500))],
    gain: 0.3,
    maxVoices: 1,
    priority: 3,
  },

  // ── Results & stingers ───────────────────────────────────────────────────────────────────────────────
  /** First down: three-note bell arpeggio. */
  first_down: {
    layers: [...bell(N.C6, 0), ...bell(N.E6, 0.09), ...bell(N.G6, 0.18, 0.4)],
    gain: 0.4,
    priority: 2,
    maxVoices: 1,
  },
  /** Touchdown fanfare stinger (G4 C5 E5 G5—). */
  td_fanfare: {
    layers: [
      ...brass(N.G4, 0, 0.07),
      ...brass(N.C5, 0.13, 0.07),
      ...brass(N.E5, 0.26, 0.07),
      ...brass(N.G5, 0.39, 0.5, 1.1),
      ...brass(N.C5, 0.39, 0.5, 0.5),
      snare(0),
      snare(0.13),
      snare(0.26),
      snare(0.39, 1.4),
      boom(0.39, 55, 0.5, 0.8),
    ],
    gain: 0.6,
    priority: 3,
    maxVoices: 1,
  },
  /** Field goal good: ding ding DING. */
  fg_good: {
    layers: [...bell(N.G5, 0), ...bell(N.B5, 0.1), ...bell(N.D6, 0.2), ...bell(N.G6, 0.3, 0.6, 1.2)],
    gain: 0.42,
    priority: 3,
    maxVoices: 1,
  },
  /** Field goal missed: sad trombone. */
  fg_miss: {
    layers: [
      L(z(1, 0, N.Eb4, 0.02, 0.16, 0.06, 2, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0.8, 0, 0, -1300)),
      L(z(1, 0, N.D4, 0.02, 0.16, 0.06, 2, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0.8, 0, 0, -1300), 0.26),
      L(z(1, 0, N.Cs4, 0.02, 0.16, 0.06, 2, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0.8, 0, 0, -1300), 0.52),
      L(z(1, 0, N.C4, 0.02, 0.6, 0.25, 2, 1, -0.05, 0, 0, 0, 0.12, 0, 0, 0, 0, 0.8, 0, 0.25, -1300), 0.78),
    ],
    gain: 0.42,
    priority: 3,
    maxVoices: 1,
  },
  /** Sack: low brass drop + boom. */
  sack: {
    layers: [
      L(z(1, 0, N.G3, 0.01, 0.2, 0.3, 2, 1, -0.3, 0, 0, 0, 0, 0, 0, 0, 0, 0.8, 0, 0, -1500)),
      boom(0, 50, 0.4),
    ],
    gain: 0.5,
    priority: 3,
    maxVoices: 1,
  },
  /** Safety: buzzer + stab. */
  safety: {
    layers: [L(z(1, 0, 110, 0.01, 0.35, 0.06, 5, 1)), ...brass(N.A3, 0.38, 0.25)],
    gain: 0.38,
    priority: 3,
    maxVoices: 1,
  },
  /** Turnover on downs: descending three tones. */
  turnover: {
    layers: [
      L(z(1, 0, N.C5, 0, 0.08, 0.08, 5, 1)),
      L(z(1, 0, N.G4, 0, 0.08, 0.08, 5, 1), 0.13),
      L(z(1, 0, N.C4, 0, 0.2, 0.2, 5, 1), 0.26),
    ],
    gain: 0.28,
    priority: 3,
    maxVoices: 1,
  },
  conversion_good: {
    layers: [...bell(N.C6, 0), ...bell(N.G6, 0.1, 0.4)],
    gain: 0.4,
    priority: 2,
    maxVoices: 1,
  },
  conversion_fail: {
    layers: [
      L(z(1, 0, N.E4, 0.01, 0.12, 0.1, 2, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0.8, 0, 0, -1200)),
      L(z(1, 0, N.C4, 0.01, 0.3, 0.2, 2, 1, -0.1, 0, 0, 0, 0, 0, 0, 0, 0, 0.8, 0, 0, -1200), 0.18),
    ],
    gain: 0.38,
    priority: 2,
    maxVoices: 1,
  },
  /** Final horn: the stadium air horn (synth fallback: low brass chord). */
  final_horn: {
    layers: [...brass(N.C4 / 2, 0, 1.0, 1), ...brass(N.G3, 0, 1.0, 0.8), ...brass(N.C4, 0, 1.0, 0.6)],
    file: 'air_horn',
    fileMode: 'prefer',
    gain: 0.5,
    priority: 3,
    maxVoices: 1,
  },
  /** Stadium air horn blast layered on the TD fanfare. */
  air_horn: {
    layers: [...brass(N.C4 / 2, 0, 0.6, 1), ...brass(N.G3, 0, 0.6, 0.8)],
    file: 'air_horn',
    fileMode: 'prefer',
    fileGain: 0.7,
    gain: 0.4,
    priority: 2,
    maxVoices: 1,
  },
  /** Celebration: party popper. */
  celebrate: {
    layers: [
      L(z(1, 0, 900, 0, 0.01, 0.06, 4, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 800)),
      L(z(1, 0, 600, 0.01, 0.18, 0.05, 0, 1, 9), 0.03, 0.5),
    ],
    gain: 0.3,
    maxVoices: 2,
  },
  /** Overtime: dramatic low sting. */
  overtime: {
    layers: [
      ...brass(N.D4, 0, 0.12),
      ...brass(N.D4, 0.18, 0.12),
      ...brass(N.A4, 0.36, 0.5, 1.1),
      boom(0.36, 50, 0.6),
    ],
    gain: 0.55,
    priority: 3,
    maxVoices: 1,
  },

  // ── Specials ─────────────────────────────────────────────────────────────────────────────────────────
  /** Special telegraph (0.3 s): charge-up riser + shimmer. */
  special_telegraph: {
    layers: [
      L(z(1, 0, 150, 0.02, 0.24, 0.04, 2, 1, 10, 0, 0, 0, 0, 0, 0, 0, 0, 0.9, 0, 0, -3500)),
      L(z(1, 0, 1200, 0.05, 0.2, 0.05, 0, 1, 9, 0, 0, 0, 0.05), 0, 0.3),
    ],
    gain: 0.45,
    priority: 3,
    maxVoices: 2,
  },
  special_hail_mary: {
    layers: [
      L(z(1, 0, N.C5, 0.05, 0.45, 0.6, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0.1, 0.8)),
      L(z(1, 0, N.E5, 0.05, 0.45, 0.6, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0.1, 0.8), 0.03, 0.8),
      L(z(1, 0, N.G5, 0.05, 0.45, 0.6, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0.1, 0.8), 0.06, 0.7),
      L(z(1, 0, N.C6, 0.05, 0.45, 0.6, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0.1, 0.8), 0.09, 0.4),
    ],
    gain: 0.5,
    priority: 3,
    maxVoices: 1,
  },
  special_bulldozer: {
    layers: [
      L(z(1, 0, 55, 0.02, 0.35, 0.15, 2, 1, 0.8, 0, 0, 0, 0.03, 0.2, 0, 0, 0, 0.8, 0, 0.5, -1500)),
      L(z(1, 0, 233, 0.01, 0.28, 0.08, 5, 1), 0.25, 0.4),
      L(z(1, 0, 277, 0.01, 0.28, 0.08, 5, 1), 0.25, 0.35),
      boom(0, 45, 0.5, 0.7),
    ],
    gain: 0.55,
    priority: 3,
    maxVoices: 1,
  },
  special_mirage: {
    layers: [
      L(z(1, 0, 520, 0.05, 0.35, 0.2, 0, 1, 2, 0, 0, 0, 0.15, 0, 3, 0, 0.08, 0.8)),
      L(z(1, 0, 780, 0.05, 0.35, 0.2, 1, 1, -2, 0, 0, 0, 0.15, 0, 3, 0, 0.08, 0.8), 0.02, 0.5),
    ],
    gain: 0.42,
    priority: 3,
    maxVoices: 1,
  },
  special_blitz: {
    layers: [
      L(z(1, 0, 600, 0.01, 0.5, 0.1, 5, 1, 8, 0, 0, 0, 0.125, 0, 0, 0, 0, 1, 0, 0, -2500)),
      boom(0, 55, 0.35, 0.7),
    ],
    gain: 0.4,
    priority: 3,
    maxVoices: 1,
  },
  /** Power meter full: shimmering arpeggio up. */
  power_full: {
    layers: [
      L(z(1, 0, N.C5, 0, 0.04, 0.08, 5, 1)),
      L(z(1, 0, N.E5, 0, 0.04, 0.08, 5, 1), 0.06),
      L(z(1, 0, N.G5, 0, 0.04, 0.08, 5, 1), 0.12),
      L(z(1, 0, N.C6, 0, 0.08, 0.25, 5, 1, 0, 0, 0, 0, 0.05, 0, 0, 0, 0, 1, 0, 0.4), 0.18),
    ],
    gain: 0.3,
    priority: 2,
    maxVoices: 1,
  },
  /** Power gained (tackle/catch/bait): coin blip. */
  power_gain: {
    layers: [L(z(1, 0, 1200, 0, 0.02, 0.12, 5, 1, 0, 0, 600, 0.04))],
    gain: 0.14,
    maxVoices: 1,
    cooldownMs: 120,
    priority: 0,
  },
  /** Feint refused (not enough stamina): "nuh-uh". */
  stamina_denied: {
    layers: [L(z(1, 0, 150, 0, 0.06, 0.04, 5, 1)), L(z(1, 0, 118, 0, 0.08, 0.05, 5, 1), 0.1)],
    gain: 0.2,
    maxVoices: 1,
    cooldownMs: 200,
  },
  /** Stamina ran out: low pulse. */
  stamina_empty: { layers: [L(z(1, 0, 200, 0.01, 0.1, 0.1, 1, 1, -0.6))], gain: 0.2, maxVoices: 1 },

  // ── UI ───────────────────────────────────────────────────────────────────────────────────────────────
  ui_move: {
    layers: [L(z(1, 0, 900, 0, 0.01, 0.03, 5, 1))],
    gain: 0.13,
    pitchVar: 0.02,
    maxVoices: 2,
    cooldownMs: 25,
    ui: true,
  },
  ui_confirm: {
    layers: [L(z(1, 0, 660, 0, 0.03, 0.08, 5, 1, 0, 0, 330, 0.04))],
    gain: 0.24,
    pitchVar: 0,
    maxVoices: 2,
    ui: true,
  },
  ui_back: {
    layers: [L(z(1, 0, 660, 0, 0.03, 0.08, 5, 1, 0, 0, -220, 0.04))],
    gain: 0.22,
    pitchVar: 0,
    maxVoices: 2,
    ui: true,
  },
  ui_error: {
    layers: [L(z(1, 0, 140, 0, 0.12, 0.05, 5, 1))],
    gain: 0.18,
    maxVoices: 1,
    cooldownMs: 120,
    ui: true,
  },
  /** PRESS START: coin + octave chime + riser. */
  ui_start: {
    layers: [
      L(z(1, 0, N.C5, 0, 0.04, 0.25, 5, 1, 0, 0, N.C5, 0.06)),
      L(z(1, 0, 300, 0.05, 0.1, 0.1, 1, 1, 8), 0, 0.35),
      ...bell(N.C6, 0.12, 0.4, 0.6),
    ],
    gain: 0.36,
    maxVoices: 1,
    ui: true,
  },
  /** Character lock-in: punch + chime. */
  ui_lock_in: {
    layers: [L(z(1, 0, 160, 0, 0.03, 0.12, 0, 1, -6)), ...bell(N.C6, 0.02, 0.22, 0.6)],
    gain: 0.4,
    maxVoices: 2,
    ui: true,
  },
  /** Broadcast transition whoosh (chyrons, versus wipe). */
  ui_swoosh: {
    layers: [L(z(1, 0, 600, 0.08, 0.05, 0.15, 4, 1, 4, 0, 0, 0, 0, 0, 0, 0, 0, 0.7, 0, 0, -5000))],
    gain: 0.28,
    maxVoices: 2,
    cooldownMs: 80,
    ui: true,
  },
  /** Countdown tick. */
  ui_tick: {
    layers: [L(z(1, 0, 1400, 0, 0.008, 0.02, 5, 1))],
    gain: 0.12,
    maxVoices: 1,
    cooldownMs: 40,
    ui: true,
  },
  /** Settings toggle: two-step click. */
  ui_toggle: {
    layers: [L(z(1, 0, 1100, 0, 0.008, 0.02, 5, 1)), L(z(1, 0, 1650, 0, 0.008, 0.03, 5, 1), 0.035, 0.8)],
    gain: 0.16,
    pitchVar: 0,
    maxVoices: 2,
    cooldownMs: 40,
    ui: true,
  },
  /** Screen/panel transition whoosh (UI_SFX.whoosh; same family as ui_swoosh, a touch brighter). */
  ui_whoosh: {
    layers: [L(z(1, 0, 700, 0.06, 0.04, 0.14, 4, 1, 5, 0, 0, 0, 0, 0, 0, 0, 0, 0.7, 0, 0, -6000))],
    gain: 0.26,
    maxVoices: 2,
    cooldownMs: 80,
    ui: true,
  },
  /** Big UI slam (versus splash, title logo, chyron slam): punch + crash + sub. */
  ui_slam: {
    layers: [
      boom(0, 62, 0.35, 1),
      L(z(1, 0, 300, 0, 0.04, 0.22, 4, 1, -2, 0, 0, 0, 0, 0, 0, 0.1), 0, 0.6),
      L(z(1, 0, 2200, 0, 0.02, 0.3, 4, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0.5, 0, 0, 3000), 0.01, 0.25),
    ],
    gain: 0.55,
    pitchVar: 0.02,
    maxVoices: 1,
    cooldownMs: 120,
    ui: true,
  },
  /** Player ready (character select / lobby): rising two-note chime. */
  ui_ready: {
    layers: [...bell(N.G5, 0, 0.14, 0.8), ...bell(N.C6, 0.08, 0.3)],
    gain: 0.34,
    pitchVar: 0,
    maxVoices: 2,
    ui: true,
  },
} as const satisfies Record<string, SfxDef>;

export type SfxId = keyof typeof SFX_BANK;
export const SFX_IDS = Object.keys(SFX_BANK) as SfxId[];

/** Read a bank definition by (untrusted) id. */
export const sfxDef = (id: string): SfxDef | undefined =>
  Object.prototype.hasOwnProperty.call(SFX_BANK, id) ? (SFX_BANK as Record<string, SfxDef>)[id] : undefined;
